import { Injectable } from '@angular/core';
import { HelpFaq, HelpSearchResult, HelpTopic, HelpTopicKey } from '@core/models/domain/help.domain';
import { HELP_TOPICS } from './help-content';

export { HELP_PAGE_ROUTE } from './help-routes';

/** Everything one entry can be matched on, as a single lowercased string. */
function haystackOf(values: string[]): string {
  return values.join(' ').toLowerCase();
}

function faqMatches(faq: HelpFaq, needle: string): boolean {
  return haystackOf([faq.question, faq.answer, ...(faq.keywords ?? [])]).includes(needle);
}

/** A topic matches on its own name and description, so searching "calendar" finds the section. */
function topicMatches(topic: HelpTopic, needle: string): boolean {
  return haystackOf([topic.title, topic.whatIsIt, ...topic.whatDoesItDo, ...topic.keywords]).includes(needle);
}

/**
 * The School App help, read from one place.
 *
 * The content itself is a constant, not a request: it ships with the app, so the Help page and the
 * contextual Help buttons render immediately and work offline.
 */
@Injectable({ providedIn: 'root' })
export class HelpService {
  readonly topics: HelpTopic[] = HELP_TOPICS;

  /** The help for one section, or undefined if the key is not one we have content for. */
  topic(key: HelpTopicKey): HelpTopic | undefined {
    return this.topics.find((topic) => topic.key === key);
  }

  /**
   * Topics and the questions of them that match `term`, case-insensitively.
   *
   * An empty search returns everything. When the section itself matches, all of its questions come
   * back, so searching for a section name reads as "here is that section", not as "no results".
   */
  search(term: string): HelpSearchResult[] {
    const needle = term.trim().toLowerCase();
    if (!needle) {
      return this.topics.map((topic) => ({ topic, faqs: topic.faqs }));
    }

    return this.topics
      .map((topic) => ({
        topic,
        faqs: topicMatches(topic, needle) ? topic.faqs : topic.faqs.filter((faq) => faqMatches(faq, needle)),
      }))
      .filter((result) => result.faqs.length > 0);
  }

  /** How many questions the results hold, for the "n results" line. */
  countQuestions(results: HelpSearchResult[]): number {
    return results.reduce((total, result) => total + result.faqs.length, 0);
  }
}
